import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab,
  Box, Stack, Grid, Typography, TextField, Checkbox, RadioGroup,
  FormControlLabel, Radio
} from '@mui/material';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ModalFormRow = ({ label, children, bold = false }) => (
  <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
    <Grid item xs={12} md={5}>
      <Typography variant="subtitle2" sx={{ fontWeight: bold ? 'bold' : 'normal', display: 'block' }}>
        {label}
      </Typography>
    </Grid>
    <Grid item xs={12} md={7}>{children}</Grid>
  </Grid>
);

export default function AddMovableAssetDialog({ open, onClose, matterId, onSave, initialData }) {
  const [tabValue, setTabValue] = useState(0);

  const [formData, setFormData] = useState(initialData || {});

  useEffect(() => {
    if (open) setFormData(initialData || {});
  }, [open, initialData]);

  const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
      }));
  };

  const handleSave = async () => {
      try {
          await axios.post(`https://demo.insollex.com/api/legal-matter/${matterId}/movable-assets`, formData);
          if (onSave) onSave();
          onClose();
      } catch (error) {
          console.error("Error saving movable asset:", error);
          alert("Failed to save movable asset.");
      }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span style={{ color: 'green' }}>●</span> Adding a Movable Asset (New)
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, bgcolor: '#fdfdfd' }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" sx={{ bgcolor: '#f0f0f0', minHeight: 32 }}>
          <Tab label="Details of Movable Asset" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
          <Tab label="Notes/Other" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
          <Tab label="Estate Duty (1)" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
          <Tab label="Estate Duty (2)" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
          <Tab label="Estimated CGT" sx={{ fontSize: '0.75rem', textTransform: 'none' }} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
            <Stack spacing={0.5}>
              <ModalFormRow label="Complete the full L&D a/c description for the asset">
                <TextField name="description" value={formData.description || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ bgcolor: '#fffde7' }} />
              </ModalFormRow>

              <ModalFormRow label="Value of asset reflected in Inventory">
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField name="inventoryValue" value={formData.inventoryValue || '0.00'} onChange={handleChange} sx={{ width: '120px' }} size="small" />
                  <Typography variant="caption">Value approved by SARS for Duty</Typography>
                  <TextField name="sarsValue" value={formData.sarsValue || '0.00'} onChange={handleChange} sx={{ width: '120px' }} size="small" />
                </Stack>
              </ModalFormRow>

              <ModalFormRow label="L&D value OR Sale price" bold>
                <TextField name="ldValue" value={formData.ldValue || '0.00'} onChange={handleChange} sx={{ width: '120px' }} size="small" />
              </ModalFormRow>

              <ModalFormRow label="Is this a special bequest?">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Checkbox name="isSpecialBequest" checked={formData.isSpecialBequest || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>(Bequest will be excluded from residue awarded ito the Will)</Typography>
                </Stack>
              </ModalFormRow>

              <ModalFormRow label="Has this movable been sold/reduced to cash?">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Checkbox name="isSold" checked={formData.isSold || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    <Typography variant="caption">If so, on which date was it sold?</Typography>
                    <TextField name="dateSold" value={formData.dateSold || ''} onChange={handleChange} size="small" sx={{ width: '150px' }} />
                </Stack>
              </ModalFormRow>

              <ModalFormRow label="To whom must it be awarded?">
                <Stack direction="row" spacing={1}>
                  <TextField name="awardedTo" value={formData.awardedTo || ''} onChange={handleChange} fullWidth multiline rows={2} size="small" />
                  <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', border: '1px solid #ccc', height: '32px' }}>Heirs</Button>
                </Stack>
              </ModalFormRow>

              <ModalFormRow label="Is this a licensed firearm?">
                <Checkbox name="isLicensedFirearm" checked={formData.isLicensedFirearm || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
              </ModalFormRow>

              <ModalFormRow label="Is this a motor vehicle to be transferred?">
                <Checkbox name="isMotorVehicle" checked={formData.isMotorVehicle || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
              </ModalFormRow>

              <Box sx={{ pl: 4, mb: 2 }}>
                  <Grid container spacing={1}>
                      <Grid item xs={12}><TextField name="licenceNumber" value={formData.licenceNumber || ''} onChange={handleChange} fullWidth size="small" placeholder="Complete the licence number" /></Grid>
                      <Grid item xs={12}><TextField name="registrationNumber" value={formData.registrationNumber || ''} onChange={handleChange} fullWidth size="small" placeholder="Complete the registration number" /></Grid>
                      <Grid item xs={12}><TextField name="vinNumber" value={formData.vinNumber || ''} onChange={handleChange} fullWidth size="small" placeholder="Complete the VIN number" /></Grid>
                      <Grid item xs={12}><TextField name="vehicleMake" value={formData.vehicleMake || ''} onChange={handleChange} fullWidth size="small" placeholder="Make of vehicle" /></Grid>
                      <Grid item xs={12}><TextField name="vehicleAcquiredDate" value={formData.vehicleAcquiredDate || ''} onChange={handleChange} fullWidth size="small" placeholder="Date on which first acquired (if known)" /></Grid>
                  </Grid>
              </Box>

              <Typography variant="caption" sx={{ fontWeight: 'bold', mt: 2 }}>Estate Duty Questions:</Typography>
              <ModalFormRow label="Is this movable property OUTSIDE the RSA?">
                <Checkbox name="outsideRSA" checked={formData.outsideRSA || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
              </ModalFormRow>
              
              <Box sx={{ pl: 2 }}>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>Is the property awarded to any of the following?</Typography>
                <RadioGroup name="awardedToRadio" value={formData.awardedToRadio || 'none'} onChange={handleChange} sx={{ ml: 1 }}>
                    <FormControlLabel value="spouse" control={<Radio size="small" />} label={<Typography variant="caption">Surviving spouse</Typography>} />
                    <FormControlLabel value="trust1" control={<Radio size="small" />} label={<Typography variant="caption">Non-discretionary trust - spouse sole capital and income beneficiary</Typography>} />
                    <FormControlLabel value="trust2" control={<Radio size="small" />} label={<Typography variant="caption">Non-discretionary trust - spouse sole income beneficiary</Typography>} />
                    <FormControlLabel value="none" control={<Radio size="small" />} label={<Typography variant="caption">None</Typography>} />
                </RadioGroup>
              </Box>
            </Stack>
        </TabPanel>
    {/* ================= TAB 2: NOTES/OTHER ================= */}
        <TabPanel value={tabValue} index={1}>
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Typography variant="caption" sx={{ fontWeight: 500, color: '#333', mt: 1, minWidth: '200px' }}>
                        Notes about this asset to be printed on the L&D account:
                    </Typography>
                    <TextField 
                        name="notesForLD" value={formData.notesForLD || ''} onChange={handleChange}
                        fullWidth 
                        multiline 
                        rows={10} 
                        variant="outlined" 
                        sx={{ bgcolor: '#fffde7' }} // Light yellow background matching image_6c14ff
                    />
                </Box>

                <Stack spacing={1} sx={{ mt: 1 }}>
                    <ModalFormRow label="Print above notes on the L&D a/c?">
                        <Checkbox name="printNotesOnLD" checked={formData.printNotesOnLD || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>

                    <ModalFormRow label="Is this asset to be excluded from the community estate?">
                        <Checkbox name="excludeFromCommunity" checked={formData.excludeFromCommunity || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>

                    <ModalFormRow label="Type of insurance over this asset?">
                        <RadioGroup row name="insuranceType" value={formData.insuranceType || 'None'} onChange={handleChange}>
                            <FormControlLabel value="Private" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Private</Typography>} />
                            <FormControlLabel value="Commercial" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">Commercial</Typography>} />
                            <FormControlLabel value="SBIB" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">SBIB</Typography>} />
                            <FormControlLabel value="None" control={<Radio size="small" sx={{ p: 0.5 }} />} label={<Typography variant="caption">None</Typography>} />
                        </RadioGroup>
                    </ModalFormRow>

                    <ModalFormRow label="Insurance Value:">
                        <TextField 
                            name="insuranceValue" value={formData.insuranceValue || '0.00'} onChange={handleChange}
                            size="small" 
                            sx={{ width: '120px', '& .MuiInputBase-input': { textAlign: 'right', py: 0.5 } }} 
                        />
                    </ModalFormRow>

                    <ModalFormRow label="Is this asset jewelery?">
                        <Checkbox name="isJewelry" checked={formData.isJewelry || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                    </ModalFormRow>

                    <ModalFormRow label="Street address where the asset is held:">
                        <TextField 
                            name="streetAddress" value={formData.streetAddress || ''} onChange={handleChange}
                            fullWidth 
                            size="small" 
                            variant="outlined" 
                            sx={{ '& .MuiInputBase-input': { py: 0.5 } }} 
                        />
                    </ModalFormRow>
                </Stack>
            </Stack>
        </TabPanel>

        {/* ================= TAB 3: ESTATE DUTY (1) ================= */}
        <TabPanel value={tabValue} index={2}>
            <Stack spacing={0.5}>
                <ModalFormRow label="Is usufruct/fiduciary or such like right being CREATED now?">
                    <Checkbox name="usufructCreated" checked={formData.usufructCreated || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="In favour of whom?">
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField name="usufructInFavourOf" value={formData.usufructInFavourOf || ''} onChange={handleChange} fullWidth multiline rows={2} variant="outlined" size="small" />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', border: '1px solid #b0b0b0' }}>Heirs</Button>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Was it donated (excluding donations ito Note 1)?">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox name="wasDonated" checked={formData.wasDonated || false} onChange={handleChange} size="small" sx={{ p: 0, color: 'red' }} />
                        <Typography variant="caption">If so, on which date?</Typography>
                        <TextField name="dateDonated" value={formData.dateDonated || ''} onChange={handleChange} size="small" sx={{ width: '120px' }} />
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Amount of donation tax paid">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField name="amountDonationTaxPaid" value={formData.amountDonationTaxPaid || '0.00'} onChange={handleChange} size="small" sx={{ width: '100px' }} />
                        <Typography variant="caption" sx={{ ml: 2 }}>Value of donation at the time</Typography>
                        <TextField name="valueOfDonation" value={formData.valueOfDonation || '0.00'} onChange={handleChange} size="small" sx={{ width: '100px' }} />
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="To whom was it donated? (Name and address)">
                    <TextField name="donatedTo" value={formData.donatedTo || ''} onChange={handleChange} fullWidth size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Period for which such right will be enjoyed">
                    <TextField name="periodEnjoyed" value={formData.periodEnjoyed || '0'} onChange={handleChange} size="small" sx={{ width: '60px' }} />
                </ModalFormRow>

                <ModalFormRow label="Yield at which value of right must be calculated">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField name="yieldForCalculation" value={formData.yieldForCalculation || '12.00'} onChange={handleChange} size="small" sx={{ width: '80px' }} />
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>%</Typography>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Value of right ito Sect 5">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField name="valueOfRightSect5" value={formData.valueOfRightSect5 || '0.00'} onChange={handleChange} size="small" sx={{ width: '100px' }} />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none' }}>Table A</Button>
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none' }}>Table B</Button>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Is this an object d'art lent to State/Local Authority?">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox name="objectDartLent" checked={formData.objectDartLent || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(Under a notarial deed for longer than 30 years)</Typography>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Is a rebate iro successive deaths claimable?">
                    <Checkbox name="rebateSuccessiveDeaths" checked={formData.rebateSuccessiveDeaths || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="If so, on which date did the predeceased die?">
                    <TextField name="datePredeceasedDied" value={formData.datePredeceasedDied || ''} onChange={handleChange} size="small" sx={{ width: '120px' }} />
                </ModalFormRow>

                <ModalFormRow label="Is it a donation/inheritance from Non-SA resident?">
                    <Checkbox name="fromNonSAResident1" checked={formData.fromNonSAResident1 || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Is it a donation/inheritance from Non-SA resident?">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox name="fromNonSAResident2" checked={formData.fromNonSAResident2 || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>(After Deceased became ordinarily resident in SA - Sect 4(e))</Typography>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Was this acquired before ordinarily resident in SA?">
                    <Checkbox name="acquiredBeforeResident" checked={formData.acquiredBeforeResident || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Is this a bequest to a charity/fund?">
                    <Checkbox name="bequestToCharity" checked={formData.bequestToCharity || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Are these shares/stocks held in Body Corporate?">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox name="sharesInBodyCorporate" checked={formData.sharesInBodyCorporate || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                        <Typography variant="caption">(NOT registered as a Company)</Typography>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Are shares/stock held in a Non-SA Company?">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox name="sharesInNonSACompany" checked={formData.sharesInNonSACompany || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                        <Typography variant="caption">(For which change of ownership of shares ISN'T recorded in SA)</Typography>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Beneficiary becomes owner by nomination agreement?">
                    <Checkbox name="nominationAgreement" checked={formData.nominationAgreement || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="NOT taken as 4q deduction in predeceased's estate, but to taken as a 4m deduction in this estate:">
                    <Checkbox name="not4qBut4m" checked={formData.not4qBut4m || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Is this property NOT owned by deceased that he could sell for his benefit?">
                    <Checkbox name="notOwnedByDeceased" checked={formData.notOwnedByDeceased || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="If so, who is the registered owner?">
                    <TextField name="registeredOwner" value={formData.registeredOwner || ''} onChange={handleChange} fullWidth size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Was this asset donated mortis causa?">
                    <Checkbox name="donatedMortisCausa" checked={formData.donatedMortisCausa || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Was it donated to Deceased by its beneficiary?">
                    <Checkbox name="donatedToDeceasedByBeneficiary" checked={formData.donatedToDeceasedByBeneficiary || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>
            </Stack>
        </TabPanel>

        {/* ================= TAB 4: ESTATE DUTY (2) ================= */}
        <TabPanel value={tabValue} index={3}>
            <Stack spacing={0.5}>
                {/* --- Fiduciary Section --- */}
                <ModalFormRow label="Did Deceased hold a fiduciary right to this asset?">
                    <Checkbox name="heldFiduciaryRight" checked={formData.heldFiduciaryRight || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>
                <ModalFormRow label="Original asset value declared in previous estate">
                    <TextField name="fiduciaryOriginalValue" value={formData.fiduciaryOriginalValue || '0.00'} onChange={handleChange} size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                </ModalFormRow>
                <ModalFormRow label="Yield for calculation of value">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <TextField name="fiduciaryYield" value={formData.fiduciaryYield || '12.000000'} onChange={handleChange} size="small" sx={{ width: '120px' }} />
                        <Typography variant="caption">%</Typography>
                    </Stack>
                </ModalFormRow>
                <ModalFormRow label="Value of right ito Sect. 5 of the Estate Duty Act">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField name="fiduciaryValueOfRight" value={formData.fiduciaryValueOfRight || '0.00'} onChange={handleChange} size="small" sx={{ width: '120px' }} />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none' }}>Table A</Button>
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none' }}>Table B</Button>
                    </Stack>
                </ModalFormRow>

                {/* --- Usufruct Section --- */}
                <ModalFormRow label="Did Deceased hold a usufruct over the asset?">
                    <Checkbox name="heldUsufructRight" checked={formData.heldUsufructRight || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>
                <ModalFormRow label="Original asset value declared in previous estate">
                    <TextField name="usufructOriginalValue" value={formData.usufructOriginalValue || '0.00'} onChange={handleChange} size="small" sx={{ width: '150px', '& .MuiInputBase-input': { textAlign: 'right' } }} />
                </ModalFormRow>
                <ModalFormRow label="Yield for calculation of value">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <TextField name="usufructYield" value={formData.usufructYield || '12.000000'} onChange={handleChange} size="small" sx={{ width: '120px' }} />
                        <Typography variant="caption">%</Typography>
                    </Stack>
                </ModalFormRow>
                <ModalFormRow label="Value of right ito Sect. 5 of the Estate Duty Act">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField name="usufructValueOfRight" value={formData.usufructValueOfRight || '0.00'} onChange={handleChange} size="small" sx={{ width: '120px' }} />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none' }}>Table A</Button>
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none' }}>Table B</Button>
                    </Stack>
                </ModalFormRow>

                {/* --- General Information Section --- */}
                <Typography variant="caption" sx={{ fontWeight: 'bold', mt: 2, display: 'block' }}>
                    General Information relating to both usufructuary/fiduciary rights above:
                </Typography>
                <ModalFormRow label="Select ultimate beneficiaries of this asset">
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField name="ultimateBeneficiaries" value={formData.ultimateBeneficiaries || ''} onChange={handleChange} fullWidth multiline rows={2} variant="outlined" size="small" />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', border: '1px solid #b0b0b0' }}>Heirs</Button>
                    </Stack>
                </ModalFormRow>
                <ModalFormRow label="Period for which ultimate beneficiary will enjoy right">
                    <TextField name="periodUltimateBeneficiaryEnjoys" value={formData.periodUltimateBeneficiaryEnjoys || '0'} onChange={handleChange} size="small" sx={{ width: '60px' }} />
                </ModalFormRow>
                <ModalFormRow label="Date on which the right was created?">
                    <TextField name="dateRightCreated" value={formData.dateRightCreated || ''} onChange={handleChange} size="small" sx={{ width: '150px' }} />
                </ModalFormRow>
                <ModalFormRow label="Complete origin of the right">
                    <TextField name="originOfRight" value={formData.originOfRight || ''} onChange={handleChange} fullWidth multiline rows={2} size="small" />
                </ModalFormRow>
                <ModalFormRow label="Describe any expenses incurred iro such right">
                    <TextField name="expensesIncurred" value={formData.expensesIncurred || ''} onChange={handleChange} fullWidth size="small" />
                </ModalFormRow>
                <ModalFormRow label="Amount of expenses">
                    <TextField name="amountOfExpenses" value={formData.amountOfExpenses || '0.00'} onChange={handleChange} size="small" sx={{ width: '150px' }} />
                </ModalFormRow>
                <ModalFormRow label="Consideration paid by heir">
                    <TextField name="considerationPaidByHeir" value={formData.considerationPaidByHeir || '0.00'} onChange={handleChange} size="small" sx={{ width: '150px' }} />
                </ModalFormRow>

                {/* --- Further Estate Duty Considerations --- */}
                <Typography variant="caption" sx={{ fontWeight: 'bold', mt: 2, display: 'block' }}>
                    Further estate duty considerations:
                </Typography>
                <ModalFormRow label="Was it sold for less than the market value?">
                    <Checkbox name="soldForLessThanMarket" checked={formData.soldForLessThanMarket || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>
                <ModalFormRow label="If so, for how much?">
                    <TextField name="soldForHowMuch" value={formData.soldForHowMuch || '0.00'} onChange={handleChange} size="small" sx={{ width: '150px' }} />
                </ModalFormRow>
                <ModalFormRow label="If so, what was the market value at date of sale?">
                    <TextField name="marketValueAtSale" value={formData.marketValueAtSale || '0.00'} onChange={handleChange} size="small" sx={{ width: '150px' }} />
                </ModalFormRow>
                <ModalFormRow label="If so, who was it sold to?">
                    <TextField name="soldToWhom" value={formData.soldToWhom || ''} onChange={handleChange} fullWidth size="small" />
                </ModalFormRow>
            </Stack>
        </TabPanel>

        {/* ================= TAB 5: ESTIMATED CGT ================= */}
        <TabPanel value={tabValue} index={4}>
            <Stack spacing={0.5}>
                <ModalFormRow 
                    label="Is this asset subject to Capital Gains Tax?" 
                    note="(For info refer to www.sars.gov.za or press F1)"
                >
                    <Checkbox name="subjectToCGT" checked={formData.subjectToCGT || false} onChange={handleChange} size="small" sx={{ p: 0 }} />
                </ModalFormRow>

                <ModalFormRow label="Base cost">
                    <TextField name="baseCost" value={formData.baseCost || '0.00'} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5, textAlign: 'right' } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="What is the calculated roll over base cost for this asset?">
                    <TextField name="rollOverBaseCost" value={formData.rollOverBaseCost || '0.00'} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5, textAlign: 'right' } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Total exclusions (NOT the annual exclusion)">
                    <TextField name="totalExclusions" value={formData.totalExclusions || '0.00'} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5, textAlign: 'right' } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Amount of Capital Gain (if applicable)">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField name="capitalGain" value={formData.capitalGain || '0.00'} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5, textAlign: 'right' } }} size="small" variant="outlined" />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #b0b0b0' }}>Calculate</Button>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Amount of Capital Loss (if applicable)">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField name="capitalLoss" value={formData.capitalLoss || '0.00'} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5, textAlign: 'right' } }} size="small" variant="outlined" />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #b0b0b0' }}>Calculate</Button>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Complete the Asset Type Source Code">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField name="assetTypeSourceCode" value={formData.assetTypeSourceCode || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                        <Typography variant="caption" sx={{ minWidth: '120px' }}>(e.g. 4250 or 4251)</Typography>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Complete the Net Capital Gain/Loss">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField name="netCapitalGainLoss" value={formData.netCapitalGainLoss || '0.00'} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5, textAlign: 'right' } }} size="small" variant="outlined" />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #b0b0b0' }}>Calculate</Button>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Complete the Inclusion Rate">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField name="inclusionRate" value={formData.inclusionRate || '0.00'} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5, textAlign: 'right' } }} size="small" variant="outlined" />
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>%</Typography>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="Complete the Taxable Capital Gain/Loss">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField name="taxableCapitalGainLoss" value={formData.taxableCapitalGainLoss || '0.00'} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5, textAlign: 'right' } }} size="small" variant="outlined" />
                        <Button variant="contained" size="small" sx={{ bgcolor: '#e0e0e0', color: '#000', textTransform: 'none', border: '1px solid #b0b0b0' }}>Calculate</Button>
                    </Stack>
                </ModalFormRow>

                <ModalFormRow label="What was the Original Purchase Price of the Asset?">
                    <TextField name="originalPurchasePrice" value={formData.originalPurchasePrice || '0.00'} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5, textAlign: 'right' } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Date purchased">
                    <TextField name="datePurchased" value={formData.datePurchased || ''} onChange={handleChange} sx={{ width: '150px', '& .MuiInputBase-input': { py: 0.5 } }} size="small" variant="outlined" />
                </ModalFormRow>

                <ModalFormRow label="Complete the method of valuation">
                    <TextField name="methodOfValuation" value={formData.methodOfValuation || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Name of Valuer">
                    <TextField name="nameOfValuer" value={formData.nameOfValuer || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>

                <ModalFormRow label="Complete Valuer's Contact Details">
                    <TextField name="valuerContactDetails" value={formData.valuerContactDetails || ''} onChange={handleChange} fullWidth size="small" variant="outlined" sx={{ '& .MuiInputBase-input': { py: 0.5 } }} />
                </ModalFormRow>
            </Stack>
        </TabPanel>

      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={handleSave} variant="contained" color="success" size="small" startIcon={<span>✓</span>} sx={{ textTransform: 'none' }}>OK</Button>
        <Button onClick={onClose} variant="contained" color="inherit" size="small" startIcon={<span>X</span>} sx={{ bgcolor: '#e0e0e0', textTransform: 'none' }}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}